import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  VStack,
  Grid,
  GridItem,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Text,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { getClients } from '../../services/clientService';
import { getConsultants } from '../../services/consultantService';
import { useAuth } from '../../contexts/AuthContext';
import JalaliDatePicker from '../JalaliDatePicker';

/**
 * فرم افزودن/ویرایش ملاقات
 * @param {Object} props - props کامپوننت
 * @param {Object} props.appointment - اطلاعات ملاقات برای ویرایش (اختیاری)
 * @param {Function} props.onSubmit - تابع ارسال فرم
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const AppointmentForm = React.forwardRef(({ appointment, onSubmit, isLoading }, ref) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const { isAdmin, isConsultant } = useAuth();

  // دریافت لیست مراجعان
  const { data: clientsData } = useQuery(['clients'], () => getClients({ limit: 100 }), {
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    cacheTime: 10 * 60 * 1000, // 10 دقیقه
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const clients = clientsData?.data?.clients || clientsData?.clients || clientsData?.data || clientsData || [];

  // دریافت لیست مشاوران
  const { data: consultantsData } = useQuery(['consultants'], () => getConsultants({ limit: 100 }), {
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    cacheTime: 10 * 60 * 1000, // 10 دقیقه
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const consultants = consultantsData?.data?.consultants || consultantsData?.consultants || consultantsData?.data || consultantsData || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      clientId: appointment?.clientId || appointment?.client?._id || '',
      consultantId: appointment?.consultantId || appointment?.consultant?._id || '',
      date: appointment?.date || '',
      time: appointment?.time || '',
      duration: appointment?.duration || 60,
      price: appointment?.cost || appointment?.price || '',
      // فیلدهای مربوط به مشاوران
      consultantNotes: appointment?.consultantNotes || '',
      companionName: appointment?.companionName || '',
      companionRelation: appointment?.companionRelation || '',
      // فیلدهای مربوط به مدیران (فقط اگر مدیر باشد)
      ...(isAdmin() && {
        status: appointment?.status || 'scheduled',
        paymentStatus: appointment?.paymentStatus || 'pending',
        meetingType: appointment?.meetingType || 'in-person',
        notes: appointment?.notes || '',
      }),
    },
    mode: 'onChange',
  });

  // تماشای تغییرات تاریخ و زمان
  const watchedDate = watch('date');
  const watchedTime = watch('time');

  useEffect(() => {

    if (watchedDate && watchedDate !== selectedDate) {
      setSelectedDate(watchedDate);
    }
    if (watchedTime && watchedTime !== selectedTime) {
      setSelectedTime(watchedTime);
    }
  }, [watchedDate, watchedTime, selectedDate, selectedTime]);

  // اضافه کردن فیلدهای جدید به فرم اگر موجود نباشند
  useEffect(() => {
    if (appointment) {
      // تنظیم فیلدهای جدید حتی اگر در appointment موجود نباشند
      setValue('companionName', appointment.companionName || '');
      setValue('companionRelation', appointment.companionRelation || '');
    }
  }, [appointment, setValue]);

  const handleFormSubmit = React.useCallback((data) => {
    onSubmit(data);
  }, [onSubmit]);

  // تابع برای submit کردن فرم از خارج
  React.useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(handleFormSubmit)();
    }
  }), [handleSubmit, handleFormSubmit]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* انتخاب مراجع و مشاور - فقط برای مدیران قابل ویرایش */}
        

        {/* نمایش نام مراجع در بالای فرم برای مشاوران */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        
          <Box p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
            <Text fontSize="lg" fontWeight="bold" color="blue.800" textAlign="center">
              مراجع: {(() => {
                // Debug: نمایش ساختار داده‌ها
                
                // بررسی ساختارهای مختلف داده‌ها
                if (appointment?.client?.firstName && appointment?.client?.lastName) {
                  return `${appointment.client.firstName} ${appointment.client.lastName}`;
                } else if (appointment?.client?.name) {
                  return appointment.client.name;
                } else if (appointment?.clientName) {
                  return appointment.clientName;
                } else if (appointment?.client?.firstName) {
                  return appointment.client.firstName;
                } else {
                  return 'نامشخص';
                }
              })()}
            </Text>
          </Box>
          <Box p={4} bg="green.50" borderRadius="md" border="1px" borderColor="blue.200">
            <Text fontSize="lg" fontWeight="bold" color="blue.800" textAlign="center">
              مشاور: {appointment?.consultant?.name}
            </Text>
          </Box>
          </Grid>
        

        {/* تاریخ و زمان - فقط برای مدیران قابل ویرایش */}
        {isAdmin() && (
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <JalaliDatePicker
                value={selectedDate}
                onChange={(value) => {
                  setSelectedDate(value);
                  setValue('date', value, { shouldValidate: true });
                }}
                label="تاریخ ملاقات"
                required={true}
                isInvalid={!!errors.date}
                errorMessage={errors.date?.message}
                placeholder="تاریخ ملاقات را انتخاب کنید"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                minDate={new Date()}
              />
              {/* Hidden input for validation */}
              <input
                {...register('date', {
                  required: 'تاریخ ملاقات الزامی است',
                })}
                type="hidden"
              />
            </GridItem>
            <GridItem>
              <FormControl isInvalid={errors.time}>
                <FormLabel>ساعت ملاقات *</FormLabel>
                <Input
                  type="time"
                  {...register('time', {
                    required: 'ساعت ملاقات الزامی است',
                  })}
                  value={selectedTime}
                  onChange={(e) => {
                    setSelectedTime(e.target.value);
                    setValue('time', e.target.value);
                  }}
                />
                <FormErrorMessage>
                  {errors.time && errors.time.message}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        )}

        {/* مدت زمان و هزینه - فقط برای مدیران قابل ویرایش */}
        {isAdmin() && (
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <FormControl isInvalid={errors.duration}>
                <FormLabel>مدت زمان (دقیقه) *</FormLabel>
                <NumberInput
                  min={15}
                  max={180}
                  step={15}
                  defaultValue={60}
                  value={watch('duration')}
                  onChange={(value) => setValue('duration', parseInt(value))}
                >
                  <NumberInputField
                    {...register('duration', {
                      required: 'مدت زمان الزامی است',
                      min: { value: 15, message: 'حداقل مدت زمان 15 دقیقه است' },
                      max: { value: 180, message: 'حداکثر مدت زمان 180 دقیقه است' },
                    })}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormErrorMessage>
                  {errors.duration && errors.duration.message}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={errors.price}>
                <FormLabel>هزینه (تومان)</FormLabel>
                <Input
                  type="number"
                  {...register('price', {
                    min: { value: 0, message: 'هزینه نمی‌تواند منفی باشد' },
                  })}
                  placeholder="مبلغ به تومان"
                />
                <FormErrorMessage>
                  {errors.price && errors.price.message}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        )}

        {/* نوع ملاقات و وضعیت - فقط برای مدیران */}
        {isAdmin() && (
          <>
            <FormControl isInvalid={errors.meetingType}>
              <FormLabel>نوع ملاقات</FormLabel>
              <Select {...register('meetingType')}>
                <option value="in-person">حضوری</option>
                <option value="phone">تلفنی</option>
                <option value="online">آنلاین</option>
              </Select>
              <FormErrorMessage>
                {errors.meetingType && errors.meetingType.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.status}>
              <FormLabel>وضعیت ملاقات</FormLabel>
              <Select {...register('status')}>
                <option value="pending">در انتظار</option>
                <option value="scheduled">تأیید شده</option>
                <option value="completed">انجام شده</option>
                <option value="cancelled">لغو شده</option>
                <option value="no-show">حضور نیافت</option>
              </Select>
              <FormErrorMessage>
                {errors.status && errors.status.message}
              </FormErrorMessage>
            </FormControl>
          </>
        )}

        {/* اطلاعات همراه - فقط برای مشاوران */}
        {isConsultant() && (
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <FormControl isInvalid={errors.companionName}>
                <FormLabel>نام و نام خانوادگی همراه</FormLabel>
                <Input
                  {...register('companionName')}
                  placeholder="نام و نام خانوادگی همراه"
                />
                <FormErrorMessage>
                  {errors.companionName && errors.companionName.message}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={errors.companionRelation}>
                <FormLabel>نسبت</FormLabel>
                <Input
                  {...register('companionRelation')}
                  placeholder="نسبت با مراجع"
                />
                <FormErrorMessage>
                  {errors.companionRelation && errors.companionRelation.message}
                </FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        )}

        {/* وضعیت پرداخت - فقط برای مدیران */}
        {isAdmin() && (
          <FormControl isInvalid={errors.paymentStatus}>
            <FormLabel>وضعیت پرداخت</FormLabel>
            <Select {...register('paymentStatus')}>
              <option value="pending">در انتظار</option>
              <option value="partial">نیمه پرداخت</option>
              <option value="paid">پرداخت شده</option>
            </Select>
            <FormErrorMessage>
              {errors.paymentStatus && errors.paymentStatus.message}
            </FormErrorMessage>
          </FormControl>
        )}

        {/* یادداشت‌های مشاور - فقط برای مشاوران */}
        {isConsultant() && (
          <FormControl isInvalid={errors.consultantNotes}>
            <FormLabel>یادداشت‌های مشاور</FormLabel>
            <Textarea
              {...register('consultantNotes')}
              placeholder="یادداشت‌های خصوصی مشاور درباره این ملاقات"
              rows={4}
            />
            <FormErrorMessage>
              {errors.consultantNotes && errors.consultantNotes.message}
            </FormErrorMessage>
          </FormControl>
        )}

        {/* یادداشت‌های عمومی - فقط برای مدیران */}
        {isAdmin() && (
          <FormControl isInvalid={errors.notes}>
            <FormLabel>یادداشت‌های عمومی</FormLabel>
            <Textarea
              {...register('notes')}
              placeholder="یادداشت‌های عمومی مربوط به ملاقات"
              rows={4}
            />
            <FormErrorMessage>
              {errors.notes && errors.notes.message}
            </FormErrorMessage>
          </FormControl>
        )}
      </VStack>
    </form>
  );
});

export default AppointmentForm; 